import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldAlert, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

export const HostDeactivateInviteSheet = ({ open, invite, onClose, onSoftCancel, onRevoke }) => {
  const [pending, setPending] = useState(null);
  const guestName = invite?.guest_name || 'this guest';

  const run = async (kind, action) => {
    if (pending) return;
    setPending(kind);
    try {
      await action(invite);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || (kind === 'soft' ? 'Could not cancel invitation' : 'Could not revoke access'));
    } finally {
      setPending(null);
    }
  };

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={pending ? undefined : onClose}
            data-testid="host-deactivate-invite-backdrop"
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 60 }}
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 260 }}
            data-testid="host-deactivate-invite-sheet"
            role="dialog"
            aria-label="Deactivate invitation"
            style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 61, background: 'var(--surface)', borderTopLeftRadius: 28, borderTopRightRadius: 28, padding: '20px 20px calc(24px + env(safe-area-inset-bottom))', boxShadow: '0 -12px 40px rgba(0,0,0,0.28)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h2 style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
                Deactivate invitation
              </h2>
              <button
                type="button"
                onClick={onClose}
                data-testid="host-deactivate-invite-close"
                aria-label="Close"
                disabled={!!pending}
                style={{ border: 'none', background: 'transparent', color: 'var(--text-2)', cursor: pending ? 'not-allowed' : 'pointer', padding: 4 }}
              >
                <X size={20} />
              </button>
            </div>
            <p style={{ margin: '0 0 16px', fontFamily: "'Inria Serif', serif", fontSize: 13, color: 'var(--text-3)', lineHeight: 1.35 }}>
              Choose how to end the invitation for {guestName}.
            </p>

            <button
              type="button"
              onClick={() => run('soft', onSoftCancel)}
              disabled={!!pending}
              data-testid="host-deactivate-invite-soft"
              style={{ display: 'flex', width: '100%', textAlign: 'left', gap: 12, padding: '14px', borderRadius: 18, border: '1px solid var(--card-border)', background: 'var(--surface-2)', marginBottom: 10, cursor: pending ? 'not-allowed' : 'pointer', opacity: pending === 'hard' ? 0.5 : 1 }}
            >
              <ShieldAlert size={22} color="#B37200" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
                  {pending === 'soft' ? 'Cancelling…' : 'Cancel invitation (soft)'}
                </p>
                <p style={{ margin: '4px 0 0', fontFamily: "'Inria Serif', serif", fontSize: 12, color: 'var(--text-3)', lineHeight: 1.3 }}>
                  Deactivates the guest QR so no more venue scans work, but keeps the invite in your history.
                </p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => run('hard', onRevoke)}
              disabled={!!pending}
              data-testid="host-deactivate-invite-hard"
              style={{ display: 'flex', width: '100%', textAlign: 'left', gap: 12, padding: '14px', borderRadius: 18, border: '1px solid rgba(221,6,8,0.28)', background: 'rgba(221,6,8,0.06)', cursor: pending ? 'not-allowed' : 'pointer', opacity: pending === 'soft' ? 0.5 : 1 }}
            >
              <Trash2 size={22} color="#DD0608" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 15, fontWeight: 700, color: '#DD0608' }}>
                  {pending === 'hard' ? 'Revoking…' : 'Revoke access permanently'}
                </p>
                <p style={{ margin: '4px 0 0', fontFamily: "'Inria Serif', serif", fontSize: 12, color: 'var(--text-3)', lineHeight: 1.3 }}>
                  Deactivates the pass AND removes the invite from your list. This cannot be undone.
                </p>
              </div>
            </button>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
};

export default HostDeactivateInviteSheet;
